import * as Yup from 'yup'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { updateMessage } from '../utils'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import { CRITICAL_BED_ID, NON_CRITICAL_BED_ID } from '../../../utils/constants'
import {
  setPatientInfo,
  selectPatientInfo,
  setAddDeviceCount,
} from '../ProrithmSlice'
import {
  searchPatient,
  editProrithmMapping,
} from '../../../services/ProrithmServices'
import {
  Box,
  Radio,
  Button,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from '@mui/material'
import {
  messages,
  constants,
  MapToIpdValues,
  IpdSearchOptions,
  ProrithmSocketMessage,
} from '../Constants'
import SearchByBed from '../SearchByBed'
import SearchByPatient from '../SearchByPatient'

interface MapToIpdFormProps {
  mapTo: string
  mappingId: string | null
  categoryType: string | null
  selectedDeviceId: string
  handleClose: () => void
  sendMessageOnSocket: (payload: ProrithmSocketMessage) => void
}

const MapToIpdForm: FC<MapToIpdFormProps> = ({
  mapTo,
  mappingId,
  categoryType,
  selectedDeviceId,
  handleClose,
  sendMessageOnSocket,
}) => {
  const dispatch = useAppDispatch()
  const patientInfo = useAppSelector(selectPatientInfo)
  const [searchByOptions, setSearchByOptions] = useState<string>('')
  const [bedDetails, setBedDetails] = useState<{
    wardName: string
    roomName: string
    bedNumber: string
  }>({
    wardName: '',
    roomName: '',
    bedNumber: '',
  })

  const formik = useFormik<MapToIpdValues>({
    initialValues: {
      location: '',
      bedType: '',
      ward: '',
      room: '',
      bed: '',
    },
    validationSchema: Yup.object({
      location: Yup.string().required(messages?.locationRequired),
      bedType: Yup.string().required(messages?.bedTypeRequired),
      ward: Yup.string().required(messages?.wardRequired),
      room: Yup.string().required(messages?.roomRequired),
      bed: Yup.string().required(messages?.bedRequired),
    }),
    onSubmit: () => {
      handleDeviceMapping()
    },
  })

  const handleSearchByOptionsChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setPatientInfo(null))
    setSearchByOptions((event.target as HTMLInputElement)?.value)
    formik?.resetForm()
  }

  const [searchQue, setSearchQue] = useState<{
    key: string
    value: string
  } | null>(null)
  const [ipdSearchData, setIpdSearchData] = useState<IpdSearchOptions[] | null>(
    null
  )

  const fetchPatientsList = useCallback(async () => {
    const type = searchQue?.key ?? ''
    const value = searchQue?.value ?? ''
    const data = await searchPatient(type, value, true)
    if (data?.success) {
      setIpdSearchData(data?.data ?? null)
      dispatch(setPatientInfo(null))
    } else {
      setIpdSearchData([])
    }
  }, [dispatch, searchQue?.key, searchQue?.value])

  useEffect(() => {
    if (searchQue) fetchPatientsList()
  }, [fetchPatientsList, searchQue])

  useEffect(() => {
    setSearchQue(null)
    setIpdSearchData(null)
    dispatch(setPatientInfo(null))
    formik?.resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapTo, dispatch])

  const handleSearch = (query: string, param: string) => {
    const trimmedQuery = query?.trim()
    if (!trimmedQuery) {
      setSearchQue(null)
      return
    }
    setSearchQue({ key: param, value: trimmedQuery })
  }

  const handleBedSelection = useCallback(
    (bedData: {
      location: string
      bedType: string
      ward: string
      room: string
      bed: string
      wardName: string
      roomName: string
      bedNumber: string
    }) => {
      formik?.setValues({
        location: bedData?.location,
        bedType: bedData?.bedType,
        ward: bedData?.ward,
        room: bedData?.room,
        bed: bedData?.bed,
      })
      setBedDetails({
        wardName: bedData?.wardName,
        roomName: bedData?.roomName,
        bedNumber: bedData?.bedNumber,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const getBedType = useCallback(() => {
    if (formik?.values?.bedType === CRITICAL_BED_ID) {
      return constants?.critical
    } else if (formik?.values?.bedType === NON_CRITICAL_BED_ID) {
      return constants?.nonCritical
    } else {
      return ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.values?.bedType])

  const handleDeviceMapping = async () => {
    const byPatientPayload = {
      categoryType: mapTo,
      patientId: patientInfo?.muhId,
      deviceId: selectedDeviceId,
      ipd: {
        locationName: patientInfo?.locationName ?? '',
        wardId: patientInfo?.wardId?.toString() ?? '',
        wardName: patientInfo?.wardName ?? '',
        roomId: patientInfo?.roomId?.toString() ?? '',
        roomName: patientInfo?.roomName ?? '',
        bedId: patientInfo?.bedId?.toString() ?? '',
        bedName: patientInfo?.bedName ?? '',
        bedType: patientInfo?.bedType ?? '',
      },
    }

    const byBedPayload = {
      categoryType: mapTo,
      patientId: patientInfo?.muhId,
      deviceId: selectedDeviceId,
      ipd: {
        locationName: formik?.values?.location,
        wardId: formik?.values?.ward?.toString(),
        wardName: bedDetails?.wardName,
        roomId: formik?.values?.room?.toString(),
        roomName: bedDetails?.roomName,
        bedId: formik?.values?.bed?.toString(),
        bedName: bedDetails?.bedNumber,
        bedType: getBedType(),
      },
    }

    const payload =
      searchByOptions === constants?.byBed ? byBedPayload : byPatientPayload

    const response = await editProrithmMapping(mappingId, payload)
    if (response?.success) {
      dispatch(setAddDeviceCount())
      toast(response?.message || messages?.deviceMappedSuccessfully, {
        position: 'bottom-center',
        hideProgressBar: true,
        closeButton: false,
        theme: 'dark',
        autoClose: 4000,
      })

      const updateMappingMessage = {
        messageType: constants?.update,
        messageContent: {
          message: updateMessage(categoryType, selectedDeviceId),
          macId: selectedDeviceId,
          category: mapTo,
        },
      }
      sendMessageOnSocket(updateMappingMessage)
      handleClose()
    }
  }

  const isFormValid = formik?.isValid && formik?.dirty

  return (
    <Box>
      <FormControl>
        <RadioGroup
          row
          value={searchByOptions}
          id='ipd-category-search-options'
          onChange={handleSearchByOptionsChange}
        >
          <FormControlLabel
            value='byPatient'
            label='Search by Patient'
            id='byPatient-value'
            control={<Radio />}
          />
          <FormControlLabel
            value='byBed'
            label='Search by Bed'
            id='byBed-value'
            control={<Radio />}
          />
        </RadioGroup>
      </FormControl>
      {searchByOptions === constants?.byPatient && (
        <SearchByPatient
          mapTo={mapTo}
          handleSearch={handleSearch}
          data={ipdSearchData}
        />
      )}
      {searchByOptions === constants?.byBed && (
        <SearchByBed onBedSelection={handleBedSelection} formik={formik} />
      )}
      <Box marginY={2} display='flex' justifySelf='flex-end' maxWidth={200}>
        <Button onClick={() => handleClose()}>{constants?.close}</Button>
        <Button
          onClick={handleDeviceMapping}
          disabled={!isFormValid && !patientInfo}
        >
          {constants?.submit}
        </Button>
      </Box>
    </Box>
  )
}

export default MapToIpdForm
